/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin AI Sandbox & Model Playground View (Task 11.2.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Square,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Terminal,
  XCircle,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useI18n } from '../i18n';
import { adminClient, ADMIN_AUTH_TOKEN_KEY } from '../lib/api-client';
import { getAdminDomains } from '../lib/urls';
import type {
  AdminAiModel,
  AdminSandboxTelemetry,
  SandboxPreset,
} from '@sofia/shared-types';

export interface AiSandboxViewProps {
  initialModelCode?: string;
}

type SandboxStatus = 'idle' | 'connecting' | 'streaming' | 'completed' | 'error';

export const AiSandboxView: React.FC<AiSandboxViewProps> = ({ initialModelCode }) => {
  const { t } = useI18n();

  // Model & Presets Data Queries
  const { data: modelsData, isLoading: isLoadingModels } = useQuery<{ success: boolean; data: AdminAiModel[] }>({
    queryKey: ['admin', 'ai-models'],
    queryFn: () => adminClient.get('/api/v1/admin/ai-models'),
  });

  const { data: presetsData } = useQuery<{ success: boolean; data: SandboxPreset[] }>({
    queryKey: ['admin', 'sandbox-presets'],
    queryFn: () => adminClient.get('/api/v1/admin/sandbox/presets'),
  });

  const models = modelsData?.data || [];
  const presets = presetsData?.data || [];

  // Configuration Form State
  const [selectedModelCode, setSelectedModelCode] = useState<string>(initialModelCode || 'gpt-4o-mini');
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('Explain how Cloudflare Workers achieve sub-millisecond cold starts.');
  const [temperature, setTemperature] = useState<number>(0.7);
  const [maxTokens, setMaxTokens] = useState<number>(1024);
  const [keySource, setKeySource] = useState<'pool' | 'custom'>('pool');
  const [customApiKey, setCustomApiKey] = useState<string>('');
  const [showSystemPrompt, setShowSystemPrompt] = useState<boolean>(false);
  const [showRawJson, setShowRawJson] = useState<boolean>(false);
  const [showReasoning, setShowReasoning] = useState<boolean>(true);

  // Execution & Streaming State
  const [status, setStatus] = useState<SandboxStatus>('idle');
  const [streamedText, setStreamedText] = useState<string>('');
  const [reasoningText, setReasoningText] = useState<string>('');
  const [telemetry, setTelemetry] = useState<AdminSandboxTelemetry | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rawEvents, setRawEvents] = useState<Array<{ event: string; timestamp: number; payload: any }>>([]);
  const [copied, setCopied] = useState<boolean>(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const consoleBottomRef = useRef<HTMLDivElement | null>(null);

  // Synchronize initial model when prop changes or models load
  useEffect(() => {
    if (initialModelCode) {
      setSelectedModelCode(initialModelCode);
    } else if (models.length > 0 && !selectedModelCode) {
      const defaultModel = models.find((m) => m.isDefault) || models[0];
      if (defaultModel) {
        setSelectedModelCode(defaultModel.code);
      }
    }
  }, [initialModelCode, models]);

  // Auto-scroll console to bottom during token streaming
  useEffect(() => {
    if (status === 'streaming') {
      consoleBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamedText, reasoningText, status]);

  // Apply a preset prompt
  const handleSelectPreset = (preset: SandboxPreset) => {
    setPrompt(preset.prompt);
    if (preset.systemPrompt) {
      setSystemPrompt(preset.systemPrompt);
      setShowSystemPrompt(true);
    }
    if (preset.temperature !== undefined) {
      setTemperature(preset.temperature);
    }
    if (preset.suggestedModel) {
      const found = models.find((m) => m.code === preset.suggestedModel);
      if (found) {
        setSelectedModelCode(found.code);
      }
    }
  };

  // Abort execution
  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus('idle');
  };

  // Run live streaming inference test
  const handleRunTest = async () => {
    if (!prompt.trim()) {
      setErrorMessage(t('admin.sandbox.validationPromptRequired'));
      return;
    }
    if (!selectedModelCode) {
      setErrorMessage(t('admin.sandbox.validationModelRequired'));
      return;
    }

    // Reset execution states
    setStatus('connecting');
    setStreamedText('');
    setReasoningText('');
    setTelemetry(null);
    setErrorMessage(null);
    setRawEvents([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const token = typeof window !== 'undefined' ? localStorage.getItem(ADMIN_AUTH_TOKEN_KEY) : null;
    const baseUrl = getAdminDomains().apiOrigin;
    const streamUrl = `${baseUrl}/api/v1/admin/sandbox/stream`;

    const requestPayload = {
      modelCode: selectedModelCode,
      prompt: prompt.trim(),
      systemPrompt: systemPrompt.trim() || undefined,
      temperature,
      maxTokens,
      customApiKey: keySource === 'custom' && customApiKey.trim() ? customApiKey.trim() : undefined,
    };

    try {
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestPayload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        throw new Error(errJson?.error?.message || errJson?.message || `HTTP ${response.status}: Inference stream failed`);
      }

      if (!response.body) {
        throw new Error('Readable stream not supported by browser environment.');
      }

      setStatus('streaming');

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = 'message';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i]?.trim();
          if (!line) continue;

          if (line.startsWith('event:')) {
            currentEvent = line.replace('event:', '').trim();
          } else if (line.startsWith('data:')) {
            const rawData = line.replace('data:', '').trim();
            try {
              const parsed = JSON.parse(rawData);

              setRawEvents((prev) => [
                ...prev.slice(-49),
                { event: currentEvent, timestamp: Date.now(), payload: parsed },
              ]);

              if (currentEvent === 'start') {
                setStatus('streaming');
              } else if (currentEvent === 'delta') {
                if (parsed.text) {
                  setStreamedText((prev) => prev + parsed.text);
                }
                if (parsed.reasoning) {
                  setReasoningText((prev) => prev + parsed.reasoning);
                }
              } else if (currentEvent === 'telemetry') {
                setTelemetry(parsed);
              } else if (currentEvent === 'done') {
                if (parsed.telemetry) {
                  setTelemetry(parsed.telemetry);
                }
                setStatus('completed');
              } else if (currentEvent === 'error') {
                setErrorMessage(parsed.message || 'Stream processing error');
                setStatus('error');
              }
            } catch {
              // Ignore partial JSON chunks
            }
          }
        }
      }

      if (status !== 'error') {
        setStatus('completed');
      }
    } catch (err: unknown) {
      if (abortController.signal.aborted) {
        setStatus('idle');
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        setErrorMessage(msg);
        setStatus('error');
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    if (!streamedText) return;
    navigator.clipboard.writeText(streamedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get selected model object
  const currentModel = models.find((m) => m.code === selectedModelCode);

  return (
    <div className="space-y-6">
      {/* 1. Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 rounded-2xl bg-surface border border-surface-border shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0 shadow-inner">
            <SophiaIcon name="flask" size={24} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-heading font-semibold text-foreground tracking-tight">
                {t('admin.sandbox.title')}
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                  status === 'streaming'
                    ? 'bg-primary/10 border-primary/30 text-primary animate-pulse'
                    : status === 'completed'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                    : status === 'error'
                    ? 'bg-destructive/10 border-destructive/30 text-destructive'
                    : status === 'connecting'
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-500 animate-pulse'
                    : 'bg-surface-elevated border-surface-border text-muted-fg'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    status === 'streaming'
                      ? 'bg-primary animate-ping'
                      : status === 'completed'
                      ? 'bg-emerald-500'
                      : status === 'error'
                      ? 'bg-destructive'
                      : status === 'connecting'
                      ? 'bg-amber-500 animate-ping'
                      : 'bg-muted-fg'
                  }`}
                />
                {status === 'streaming' && t('admin.sandbox.statusStreaming')}
                {status === 'connecting' && t('admin.sandbox.statusConnecting')}
                {status === 'completed' && t('admin.sandbox.statusCompleted')}
                {status === 'error' && t('admin.sandbox.statusError')}
                {status === 'idle' && t('admin.sandbox.statusIdle')}
              </span>
            </div>
            <p className="text-xs text-muted-fg mt-1 max-w-2xl">
              {t('admin.sandbox.subtitle')}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {status === 'streaming' || status === 'connecting' ? (
            <button
              onClick={handleAbort}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20 transition-all shadow-sm"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
              {t('admin.sandbox.btnStop')}
            </button>
          ) : (
            <button
              onClick={handleRunTest}
              disabled={isLoadingModels || !prompt.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:opacity-90 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SophiaIcon name="algorithm" size={14} className="fill-current" />
              {t('admin.sandbox.btnRun')}
            </button>
          )}

          <button
            onClick={() => {
              setStreamedText('');
              setReasoningText('');
              setTelemetry(null);
              setErrorMessage(null);
              setStatus('idle');
            }}
            title={t('admin.sandbox.btnClear')}
            className="p-2.5 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-all"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 2. Presets Quick Selector */}
      {presets.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          <span className="text-xs font-medium text-muted-fg whitespace-nowrap flex items-center gap-1 pl-1">
            <SophiaIcon name="brainstorm" size={14} className="text-primary shrink-0" />
            {t('admin.sandbox.presets')}:
          </span>
          {presets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handleSelectPreset(preset)}
              className="px-3 py-1.5 rounded-xl text-xs bg-surface border border-surface-border hover:border-primary/40 text-foreground/80 hover:text-primary whitespace-nowrap transition-all shadow-2xs hover:bg-primary/5"
            >
              <span className="font-medium">{preset.title}</span>
              {preset.suggestedModel && (
                <span className="ml-1.5 text-[10px] text-muted-fg">({preset.suggestedModel})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* 3. Main Workspace: Two-Column Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Model Parameters & Prompt Inputs (5 Cols) */}
        <div className="lg:col-span-5 space-y-5">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-surface-border pb-3">
              <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
                <SophiaIcon name="cpu" size={16} className="text-primary shrink-0" />
                {t('admin.sandbox.modelSelect')}
              </h2>
              {currentModel && (
                <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-surface-elevated border border-surface-border text-muted-fg">
                  x{currentModel.creditMultiplier} Credits
                </span>
              )}
            </div>

            {/* Model Selector Dropdown */}
            <div>
              <label className="block text-xs font-medium text-foreground/80 mb-1.5">
                {t('admin.sandbox.modelSelect')}
              </label>
              <select
                value={selectedModelCode}
                onChange={(e) => setSelectedModelCode(e.target.value)}
                disabled={status === 'streaming'}
                className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all font-mono"
              >
                {models.map((model) => (
                  <option key={model.code} value={model.code}>
                    {model.name} ({model.code}) - {model.providerName || model.providerCode}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-fg mt-1">
                {t('admin.sandbox.modelSelectHelp')}
              </p>
            </div>

            {/* Parameter Sliders */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-medium text-foreground/80">
                    {t('admin.sandbox.temperature')}
                  </span>
                  <span className="text-xs font-mono font-semibold text-primary">{temperature}</span>
                </div>
                <input
                  type="range"
                  min="0.0"
                  max="2.0"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  disabled={status === 'streaming'}
                  className="w-full accent-primary h-1.5 bg-surface-border rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-medium text-foreground/80">
                    {t('admin.sandbox.maxTokens')}
                  </span>
                  <span className="text-xs font-mono font-semibold text-primary">{maxTokens}</span>
                </div>
                <input
                  type="range"
                  min="64"
                  max="4096"
                  step="64"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value, 10))}
                  disabled={status === 'streaming'}
                  className="w-full accent-primary h-1.5 bg-surface-border rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>

            {/* API Key Source Selection */}
            <div className="pt-2 border-t border-surface-border space-y-3">
              <span className="block text-xs font-medium text-foreground/80">
                {t('admin.sandbox.keySource')}
              </span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setKeySource('pool')}
                  className={`px-3 py-2 rounded-xl text-xs font-medium border text-left transition-all ${
                    keySource === 'pool'
                      ? 'bg-primary/10 border-primary text-primary shadow-xs'
                      : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <SophiaIcon name="finger-scanner" size={14} className="shrink-0" />
                    <span>System KeyPool</span>
                  </div>
                  <div className="text-[10px] text-muted-fg mt-0.5 truncate">Edge KV Rotation</div>
                </button>

                <button
                  type="button"
                  onClick={() => setKeySource('custom')}
                  className={`px-3 py-2 rounded-xl text-xs font-medium border text-left transition-all ${
                    keySource === 'custom'
                      ? 'bg-primary/10 border-primary text-primary shadow-xs'
                      : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <SophiaIcon name="filter" size={14} className="shrink-0" />
                    <span>Custom API Key</span>
                  </div>
                  <div className="text-[10px] text-muted-fg mt-0.5 truncate">Direct Pre-Validation</div>
                </button>
              </div>

              {keySource === 'custom' && (
                <div className="space-y-1 pt-1 animate-fadeIn">
                  <input
                    type="password"
                    value={customApiKey}
                    onChange={(e) => setCustomApiKey(e.target.value)}
                    placeholder={t('admin.sandbox.customKeyPlaceholder')}
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                  <p className="text-[10px] text-muted-fg">
                    {t('admin.sandbox.customKeyHelp')}
                  </p>
                </div>
              )}
            </div>

            {/* Optional System Prompt Collapsible */}
            <div className="pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                className="flex items-center justify-between w-full text-xs font-medium text-muted-fg hover:text-foreground transition-all"
              >
                <span className="flex items-center gap-1.5">
                  <Terminal className="w-3.5 h-3.5" />
                  {t('admin.sandbox.systemPrompt')}
                  {systemPrompt.trim() && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </span>
                {showSystemPrompt ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {showSystemPrompt && (
                <div className="mt-2 animate-fadeIn">
                  <textarea
                    rows={3}
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    placeholder={t('admin.sandbox.systemPromptPlaceholder')}
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-y font-mono leading-relaxed"
                  />
                </div>
              )}
            </div>

            {/* User Prompt Input */}
            <div className="pt-2 border-t border-surface-border space-y-1.5">
              <label className="block text-xs font-medium text-foreground/80">
                {t('admin.sandbox.prompt')}
              </label>
              <textarea
                rows={6}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('admin.sandbox.promptPlaceholder')}
                disabled={status === 'streaming'}
                className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-y font-sans leading-relaxed shadow-inner"
              />
              <div className="flex justify-between items-center text-[11px] text-muted-fg">
                <span>{prompt.length} characters</span>
                <button
                  type="button"
                  onClick={() => setPrompt('')}
                  className="hover:text-foreground transition-all"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Telemetry Dashboard & Live Console (7 Cols) */}
        <div className="lg:col-span-7 space-y-5">
          {/* Real-Time Telemetry KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* 1. TTFT Card */}
            <div className="p-3.5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-1">
              <div className="flex items-center justify-between text-muted-fg">
                <span className="text-[11px] font-medium flex items-center gap-1.5">
                  <SophiaIcon name="hoverboard" size={13} className="text-primary shrink-0" />
                  TTFT
                </span>
                {telemetry && (
                  <span
                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                      telemetry.ttftMs < 500
                        ? 'bg-emerald-500/10 text-emerald-500'
                        : telemetry.ttftMs < 1500
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-rose-500/10 text-rose-500'
                    }`}
                  >
                    {telemetry.ttftMs < 500 ? 'Fast' : telemetry.ttftMs < 1500 ? 'Normal' : 'High'}
                  </span>
                )}
              </div>
              <div className="text-lg font-heading font-bold text-foreground font-mono">
                {telemetry ? `${telemetry.ttftMs}ms` : '--'}
              </div>
              <p className="text-[10px] text-muted-fg truncate">Time to first token</p>
            </div>

            {/* 2. Generation Speed Card */}
            <div className="p-3.5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-1">
              <div className="flex items-center justify-between text-muted-fg">
                <span className="text-[11px] font-medium flex items-center gap-1.5">
                  <SophiaIcon name="cpu" size={13} className="text-primary shrink-0" />
                  Throughput
                </span>
              </div>
              <div className="text-lg font-heading font-bold text-foreground font-mono">
                {telemetry ? `${telemetry.tokensPerSecond}` : '--'}
                <span className="text-xs font-normal text-muted-fg ml-1">t/s</span>
              </div>
              <p className="text-[10px] text-muted-fg truncate">Tokens per second</p>
            </div>

            {/* 3. Tokens Count Card */}
            <div className="p-3.5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-1">
              <div className="flex items-center justify-between text-muted-fg">
                <span className="text-[11px] font-medium flex items-center gap-1.5">
                  <SophiaIcon name="big-data" size={13} className="text-primary shrink-0" />
                  Tokens
                </span>
              </div>
              <div className="text-lg font-heading font-bold text-foreground font-mono">
                {telemetry ? telemetry.totalTokens : '--'}
              </div>
              <p className="text-[10px] text-muted-fg truncate">
                {telemetry ? `In: ${telemetry.inputTokens} | Out: ${telemetry.outputTokens}` : 'In / Out usage'}
              </p>
            </div>

            {/* 4. Total Duration & Cost Card */}
            <div className="p-3.5 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-1">
              <div className="flex items-center justify-between text-muted-fg">
                <span className="text-[11px] font-medium flex items-center gap-1.5">
                  <SophiaIcon name="battery" size={13} className="text-primary shrink-0" />
                  Cost
                </span>
                {telemetry?.gatewayCached && (
                  <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-500">
                    Cached
                  </span>
                )}
              </div>
              <div className="text-lg font-heading font-bold text-foreground font-mono">
                {telemetry ? `${telemetry.estimatedCostCredits}` : '--'}
                <span className="text-xs font-normal text-muted-fg ml-1">cr</span>
              </div>
              <p className="text-[10px] text-muted-fg truncate">
                {telemetry ? `${telemetry.durationMs}ms total` : 'Calculated credits'}
              </p>
            </div>
          </div>

          {/* Live Output Console */}
          <div className="rounded-2xl bg-surface border border-surface-border shadow-xs overflow-hidden flex flex-col min-h-[460px]">
            {/* Console Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border bg-surface-elevated/50">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-primary" />
                <span className="text-xs font-heading font-semibold text-foreground">
                  {t('admin.sandbox.outputConsole')}
                </span>
                {status === 'streaming' && (
                  <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowRawJson(!showRawJson)}
                  className={`px-2 py-1 rounded-lg text-[11px] border transition-all ${
                    showRawJson
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-surface border-surface-border text-muted-fg hover:text-foreground'
                  }`}
                >
                  Raw SSE
                </button>

                <button
                  type="button"
                  onClick={handleCopyOutput}
                  disabled={!streamedText}
                  className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-all disabled:opacity-40"
                  title={t('admin.sandbox.btnCopy')}
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Error Alert Display */}
            {errorMessage && (
              <div className="m-4 p-3.5 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2.5">
                <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-semibold">Inference Error</p>
                  <p className="font-mono text-[11px]">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Console Body */}
            <div className="p-5 flex-1 overflow-y-auto space-y-4 font-mono text-xs max-h-[560px]">
              {/* Reasoning Accordion (if model emitted chain of thought) */}
              {reasoningText && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowReasoning(!showReasoning)}
                    className="w-full flex items-center justify-between px-3.5 py-2 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/15 transition-all"
                  >
                    <span className="flex items-center gap-1.5">
                      <SophiaIcon name="mind" size={14} className="text-primary shrink-0" />
                      {t('admin.sandbox.reasoningTitle')}
                    </span>
                    {showReasoning ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {showReasoning && (
                    <div className="p-3.5 text-foreground/80 whitespace-pre-wrap font-sans text-xs leading-relaxed border-t border-primary/10">
                      {reasoningText}
                    </div>
                  )}
                </div>
              )}

              {/* Streaming Content */}
              {streamedText ? (
                <div className="text-foreground whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {streamedText}
                  {status === 'streaming' && (
                    <span className="inline-block w-1.5 h-4 ml-1 bg-primary animate-pulse align-middle" />
                  )}
                </div>
              ) : status === 'streaming' || status === 'connecting' ? (
                <div className="flex items-center justify-center py-16 text-muted-fg text-xs space-x-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                  <span>{t('admin.sandbox.statusConnecting')}</span>
                </div>
              ) : !errorMessage ? (
                <div className="text-center py-20 text-muted-fg space-y-2">
                  <Terminal className="w-8 h-8 mx-auto text-muted-fg/40 stroke-1" />
                  <p className="text-xs max-w-sm mx-auto">
                    {t('admin.sandbox.emptyOutput')}
                  </p>
                </div>
              ) : null}

              {/* Raw JSON inspection view */}
              {showRawJson && rawEvents.length > 0 && (
                <div className="pt-4 border-t border-surface-border mt-4">
                  <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider block mb-2">
                    Raw SSE Stream Events ({rawEvents.length})
                  </span>
                  <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border text-[11px] font-mono text-muted-fg max-h-48 overflow-y-auto space-y-1">
                    {rawEvents.map((evt, idx) => (
                      <div key={idx} className="border-b border-surface-border/50 pb-1">
                        <span className="text-primary font-bold">[{evt.event}]</span>{' '}
                        <span className="text-foreground/70">{JSON.stringify(evt.payload)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div ref={consoleBottomRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
